import React, { useState, useEffect } from 'react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider, useTheme } from './ThemeContext';
import AnimatedCursor from 'react-animated-cursor';

// Remove the old ReactDOM import
// import ReactDOM from 'react-dom';

// Import createRoot correctly
import { createRoot } from 'react-dom/client';

// Create a wrapper component that holds the AnimatedCursor
const AppWithCursor = () => {
  const [isHoveringLink, setIsHoveringLink] = useState(false);
  const [isHoveringCover, setIsHoveringCover] = useState(false);
  const [cursorType, setCursorType] = useState('default');
  const { isDarkMode } = useTheme();

  // Add event listeners to track when hovering over links
  useEffect(() => {
    const handleMouseOver = (e) => {
      // Check if the element or its parent is a link or has gooey-link class
      let target = e.target;
      let isLink = false;
      let isCoverImage = false;
      
      // Check up to 3 levels of parent elements
      for (let i = 0; i < 3; i++) {
        if (!target) break;
        
        if (
          target.tagName === 'A' || 
          target.classList?.contains('gooey-link') ||
          target.classList?.contains('skill-item')
        ) {
          isLink = true;
          break;
        }
        
        // Check if it's a cover image or video container
        if (
          target.classList?.contains('hinge-cover') ||
          target.classList?.contains('kins-cover') ||
          target.classList?.contains('keen-cover') ||
          target.classList?.contains('kincaid-cover') ||
          target.classList?.contains('handscroll-cover') ||
          target.classList?.contains('geists-cover') ||
          target.classList?.contains('video-container')
        ) {
          isCoverImage = true;
          break;
        }
        
        target = target.parentElement;
      }
      
      setIsHoveringLink(isLink);
      setIsHoveringCover(isCoverImage);
      setCursorType(isLink || isCoverImage ? 'pointer' : 'default');
    };
    
    const handleMouseOut = () => {
      setIsHoveringLink(false);
      setIsHoveringCover(false);
      setCursorType('default');
    };
    
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    
    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, []);

  // Determine cursor content based on type
  const getCursorContent = () => {
    if (cursorType === 'pointer') {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          fontSize: '12px',
          fontWeight: 'bold',
          color: isDarkMode ? '#000000' : '#FFFFFF'
        }}>
          View
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    // Detect if device is touch-enabled
    const isTouchDevice = 'ontouchstart' in window || 
                          navigator.maxTouchPoints > 0 || 
                          navigator.msMaxTouchPoints > 0;
    
    // If it's a touch device, don't show the custom cursor
    if (isTouchDevice) {
      // Find the cursor elements and hide them
      const cursorElements = document.querySelectorAll('.animated-cursor');
      cursorElements.forEach(el => {
        el.style.display = 'none';
      });
      
      // Add a class to the body to ensure touch scrolling works
      document.body.classList.add('touch-device');
    }
  }, []);

  return (
    <>
      <AnimatedCursor
        color={isDarkMode ? '255,255,255' : '45,45,45'}
        innerSize={isHoveringCover ? 0 : (cursorType === 'pointer' ? 8 : 8)}
        outerSize={(isHoveringLink || isHoveringCover) ? 0 : 32}
        innerScale={1.2}
        outerScale={2}
        outerAlpha={(isHoveringLink || isHoveringCover) ? 0 : 1}
        hasBlendMode={true}
        innerStyle={{
          backgroundColor: cursorType === 'pointer' ? 
            (isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(45,45,45,0.9)') : 
            (isDarkMode ? 'rgb(255,255,255)' : 'rgb(45,45,45)'),
          mixBlendMode: 'normal',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: cursorType === 'pointer' ? 
            (isDarkMode ? '2px solid rgba(255,255,255,0.5)' : '2px solid rgba(45,45,45,0.5)') : 
            'none',
          boxShadow: cursorType === 'pointer' ? '0 0 10px rgba(0,0,0,0.1)' : 'none',
          opacity: isHoveringCover ? 0 : 1,
        }}
        outerStyle={{
          mixBlendMode: 'exclusion',
          opacity: (isHoveringLink || isHoveringCover) ? 0 : 1,
          transition: 'opacity 0.2s ease'
        }}
        clickables={[
          'a',
          'input[type="text"]',
          'input[type="email"]',
          'input[type="number"]',
          'input[type="submit"]',
          'input[type="image"]',
          'label[for]',
          'select',
          'textarea',
          'button',
          '.link',
          '.gooey-link',
          '.skill-item'
        ]}
      >
        {getCursorContent()}
      </AnimatedCursor>
      
      <App />
    </>
  );
};

// Define the root element
const root = createRoot(document.getElementById('root'));

// Use the root to render your app wrapped in Router
root.render(
  <React.StrictMode>
    <Router>
      <ThemeProvider>
        <AppWithCursor />
      </ThemeProvider>
    </Router>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();